<script>
  import { onMount } from 'svelte'
  import { slide } from 'svelte/transition'
  import { auth } from '../../services/auth.js'
  import { user } from '../../models/user.js'
  import Alert from '../../components/Alert.svelte'
  import Carousel from '../../components/Carousel.svelte'
  import { track } from '../../services/analytics.js'
  import { formatPrice } from '../../services/utilities.js'
  import { pendingPlan, pendingEmail } from '../../models/pendingPlan.js'

  var reasons = [
    {
      id: 1,
      img: '/assets/reg_history.svg',
      reason: 'View Your Order History',
    },
    {
      id: 2,
      img: '/assets/reg_rent.svg',
      reason: 'Rent Puzzles',
    },
    {
      id: 3,
      img: '/assets/reg_discover.svg',
      reason: 'Discover Upcoming Deals',
    },
  ]

  var email = ''
  var password = ''
  var firstName = ''
  var lastName = ''
  var submitting = false
  var success = false
  var errorMsg = null
  let referrer = '/'
  let plan = null
  let verifyCode = ''
  let verifying = false
  let resendSuccess = false

  onMount(() => {
    let urlParams = new URLSearchParams(window.location.search)
    referrer = urlParams.get('referrer')

    if (!referrer) referrer = '/'

    plan = pendingPlan.get()

    track('sign_up_start', { referrer, has_pending_plan: !!plan })
  })

  function validateForm() {
    errorMsg = null
    var user = document.getElementById('email')
    var pass = document.getElementById('password')
    var fname = document.getElementById('firstName')
    var lname = document.getElementById('lastName')

    if (!fname.value || !lname.value) {
      errorMsg = 'Please enter a first and last name!'
      return false
    }

    if (!user.checkValidity()) {
      errorMsg = 'You have entered an invalid email address!'
      return false
    }

    if (!pass.checkValidity()) {
      errorMsg = 'Your password does not meet the minimum requirements!'
      return false
    }

    email = user.value
    password = pass.value
    firstName = fname.value
    lastName = lname.value
    return true
  }

  function submitForm(e) {
    if (e && e.key !== 'Enter' && e.type !== 'click') return
    if (validateForm()) {
      submitting = true
      success = false
      errorMsg = null

      auth.registerUser(email, password, firstName, lastName, accountCreated)
    }
  }

  function accountCreated(result, message) {
    submitting = false

    if (result) {
      success = true
      track('sign_up', { method: 'email' })
      // if they leave and verify from the sign in page later, prefill the email there
      pendingEmail.save(email)
    } else {
      errorMsg = message
    }
  }

  // Verify the emailed code here and sign in with the password still in
  // memory, so the customer doesn't retype their email and password.
  function submitCode(e) {
    if (e && e.key !== 'Enter' && e.type !== 'click') return
    errorMsg = null
    resendSuccess = false

    if (!verifyCode.trim()) {
      errorMsg = 'Please enter the verification code from your email.'
      return
    }

    verifying = true
    auth.confirmAndSignIn(email, password, verifyCode.trim(), signedIn)
  }

  function signedIn(result, message) {
    verifying = false

    if (!result) {
      errorMsg = message
      return
    }

    track('email_verified')
    password = ''
    pendingEmail.clear()
    user.login()

    // a saved plan takes them straight to checkout, which adds it to the cart.
    // Save it again in case it expired while they were finding the email.
    if (plan) {
      pendingPlan.save(plan.product, plan.durationType)
      window.location.href = '/checkout'
    } else if (
      referrer.startsWith('/') &&
      !referrer.startsWith('//') &&
      !/^\/(login|register|logout|reset)/.test(referrer)
    ) {
      window.location.href = referrer
    } else {
      window.location.href = '/'
    }
  }

  function resendCode() {
    errorMsg = null
    resendSuccess = false
    auth.resendVerification((result, message) => {
      if (result) resendSuccess = true
      else errorMsg = message
    }, email)
  }

  function redirectLogin() {
    window.location.href = `/login?referrer=${referrer}&verifyCode=`
  }
</script>

<style type="text/less">
  @media (min-width: 800px) {
    .register {
      padding: var(--spacing);
    }
  }

  .register {
    display: flex;
    max-width: var(--content-width);
    margin: auto;
    padding: var(--spacing-sm);
  }

  .user-cont,
  .user-info {
    display: block;
    flex: 0 0 50%;
    box-sizing: border-box;
  }

  .user-reasons {
    margin-bottom: var(--spacing-lg);
  }

  @media (max-width: 1050px) {
    .user-cont {
      overflow: auto;
      flex-direction: column-reverse;
    }
    /* form first on small screens; the benefits carousel can follow */
    .register {
      flex-direction: column;
    }
  }

  .names {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-sm);
  }

  .names .form-field {
    width: 100%;
  }

  /* long email addresses must wrap instead of widening the page on phones */
  .code-email {
    overflow-wrap: anywhere;
  }

  .code-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--spacing-xs) var(--spacing);
  }

  .code-links .btn {
    white-space: normal;
  }

  .pending-plan {
    padding: var(--spacing-sm);
    margin-bottom: var(--spacing-sm);
    border-radius: 8px;
    background-color: var(--col-accent-1-xl);
  }
</style>

<div class="register">
  <div class="user-cont" transition:slide|local>
    <h1 class="user-title">
      {#if success}Check your email{:else if plan}Create your free account to continue{:else}Register{/if}
    </h1>

    <div class="user-form">
      {#if plan}
        <div class="pending-plan">
          <p>
            Your plan: <b>{plan.product.title.replace(' Plan', '')}</b>
            - {formatPrice(plan.product.price)} / {plan.durationType}
          </p>
          <p>
            No charge yet. After you verify your email, we'll take you straight
            to checkout.
          </p>
        </div>
      {/if}

      {#if !success}
        <p>
          <b>
            Welcome! If you are an existing customer from the old website, please
            <a href="/reset">reset your password here!</a>
          </b>
        </p>
      {/if}

      {#if errorMsg}
        <p>
          <Alert type="danger" icon="warning">{errorMsg}</Alert>
        </p>
      {/if}

      {#if resendSuccess}
        <p>
          <Alert type="success" icon="check">Verification email sent!</Alert>
        </p>
      {/if}

      {#if success}
        <form on:submit|preventDefault={() => submitCode()}>
          <p>
            We emailed a verification code to <b class="code-email">{email}</b>. Enter it below to
            finish creating your account. Check your spam folder if you don't
            see it.
          </p>
          <div class="form-field">
            <label for="verifyCode">Verification code</label>
            <input
              class="form-input"
              type="text"
              id="verifyCode"
              inputmode="numeric"
              autocomplete="one-time-code"
              bind:value={verifyCode}
              required />
          </div>
          <div class="form-field">
            <button
              class="btn btn-expanded"
              type="submit"
              disabled={verifying}>
              {#if verifying}
                <svg class="icon spin">
                  <use href="/icons.svg#circle_notch" />
                </svg>
                Verifying...
              {:else if plan}Verify & continue to checkout{:else}Verify & sign in{/if}
            </button>
          </div>
        </form>
        <div class="user-form-links code-links">
          <button class="btn btn-link" on:click={resendCode}>
            Resend code
          </button>
          <button class="btn btn-link" on:click={redirectLogin}>
            Verify from the sign in page instead
          </button>
        </div>
      {/if}

      {#if !success}
        <form>
          <div class="names">
            <div class="form-field">
              <label for="firstName">First Name</label>
              <input class="form-input" type="text" id="firstName" required />
            </div>
            <div class="form-field">
              <label for="lastName">Last Name</label>
              <input class="form-input" type="text" id="lastName" required />
            </div>
          </div>
          <div class="form-field">
            <label for="email">Email</label>
            <input class="form-input" type="email" id="email" required />
          </div>
          <div class="form-field">
            <label class="form-field-input" for="password">Password</label>
            <p class="form-field-message">
              Minimum 8 characters, a number, a lowercase and uppercase letter,
              and a special character.
            </p>
            <input
              class="form-input"
              type="password"
              id="password"
              pattern={'(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^$*.\\[\\]{}()?-“!@#%&/,><’:;|_~`])\\S{8,99}'}
              required />
          </div>

          <div class="form-field">
            <button
              class="btn btn-expanded"
              class:btn-success={success}
              on:click|preventDefault={() => submitForm()}
              disabled={submitting}>
              {#if submitting}
                <svg class="icon spin">
                  <use href="/icons.svg#circle_notch" />
                </svg>
                Submitting...
              {:else if plan}Create account & continue{:else}Create Account{/if}
            </button>
          </div>
        </form>
      {/if}

    </div>

    {#if !success}
      <div class="user-form-links text-center">
        Already a member?
        <a href={`/login?referrer=${referrer}`}>Sign In</a>
      </div>
    {/if}
  </div>

  <div class="user-info">
    <h1 class="h1 text-center">Benefits of Registering</h1>

    <div class="user-reasons">
      <Carousel delay={false} panels={reasons}>
        <div slot="panel" let:panel>
          <img
            class=""
            alt={'Photo of ' + panel.reason}
            style="display: block; height: 20vh; margin: 2vh auto;"
            src={panel.img} />
          <h2 class="text-center">{panel.reason}</h2>
        </div>
      </Carousel>
    </div>
  </div>
</div>
